import { OrganizationMembership, Team, User } from '@/models/user.models';
import {
  FindItemsData,
  Folder,
  ItemType,
  Label,
  Lane,
  Priority,
  WorkItem,
  WorkType,
  Board,
  ItemAggregations,
  CreateFolder,
  CreateWorkItem,
  UpdateWorkItem,
  UpdateFolder,
  CustomField,
  Relationship,
} from '@/api';

/** @transient */
export type TimeEstimate = Pick<
  WorkItem,
  | 'optimisticEstimate'
  | 'likelyEstimate'
  | 'pessimisticEstimate'
  | 'estimate'
  | 'estimateSd'
  | 'estimateConfidenceTable'
>;

/** @transient */
export type NumberOnlyAggregations = Omit<
  ItemAggregations,
  'totalEstimateCompletedConfidenceTable' | 'totalEstimateConfidenceTable'
>;

/** @transient */
export type WorkTypeAggregation = {
  workType: WorkType;
  aggregations: NumberOnlyAggregations;
};

/** @transient */
export type PreparedFolder = Omit<Folder, 'itemType'> & {
  itemType: ItemType.FOLDER;
  author: OrganizationMembership;
  participantUsers: Array<User>;
  teams?: Team[];
  baseItemAggregations: NumberOnlyAggregations;
  baseArchivedItemAggregations: NumberOnlyAggregations;
  totalItemCount: number;
  totalArchivedItemCount: number;
  canHaveChildren: true;
};

/** @transient */
export type PreparedWorkItem = Omit<WorkItem, 'itemType'> & {
  itemType: ItemType.WORKITEM;
  author: OrganizationMembership;
  workType: WorkType;
  assignee?: OrganizationMembership;
  labels?: Array<Label['id']>;
  lane?: Lane;
  participantUsers: Array<OrganizationMembership>;
  team?: Team | 'private';
  board?: Board;
  priority: Priority | undefined;
  baseItemAggregations: NumberOnlyAggregations;
  baseArchivedItemAggregations: NumberOnlyAggregations;
  totalItemCount: number;
  totalArchivedItemCount: number;
  canHaveChildren: boolean;
};

/** @transient */
export type PreparedItem = PreparedWorkItem | PreparedFolder;

/** @transient */
export type PreparedSearchResultItem = PreparedItem & {
  taggedNumber: string;
  taggedTitle: string;
};

/** @transient */
export type FindItemsSortOption =
  | 'title'
  | 'assigneeUsername'
  | 'priority'
  | 'lane'
  | 'dateAdded'
  | 'dateUpdated'
  | 'startDate'
  | 'dueDate'
  | 'completedAt'
  | 'authorUsername'
  | 'pointEffort'
  | 'timeSpentSeconds'
  | 'team'
  | 'board'
  | 'subItemsCount'
  | 'labelsCount'
  | 'relationshipsCount'
  | 'estimate'
  | 'workType'
  | 'laneRank'
  | 'parentRank'
  | 'boardRank'
  | `customField:${CustomField['id']}`;

/** @transient */
export type FindItemsSortOptionsWithoutRanks = Exclude<
  FindItemsSortOption,
  'laneRank' | 'parentRank' | 'boardRank'
>;

/** @transient */
export type FindFoldersSortOption = Extract<
  FindItemsSortOption,
  'title' | 'dateAdded' | 'dateUpdated' | 'parentRank'
>;

/** @transient */
export type FindItemSortWithDirection = {
  sort: FindItemsSortOption;
  direction: 'asc' | 'desc';
};

/** @transient */
export type FindFolderSortWithDirection = {
  sort: FindFoldersSortOption;
  direction: 'asc' | 'desc';
};

/** @transient */
export type StoredQueryItem = {
  workTypeId: string;
  itemType: ItemType;
  number: string;
  teamId?: string;
};

/** @transient */
export type StoredQueryOptions = NonNullable<FindItemsData['query']> & {
  items?: Array<StoredQueryItem>;
  // Length filter fields - used for Cube TimeEntries queries only (not backend API)
  lengthSecondsGTE?: number;
  lengthSecondsLTE?: number;
};

/** @transient */
export type CreateItem = CreateFolder | CreateWorkItem;
/** @transient */
export type UpdateItem = UpdateWorkItem | UpdateFolder;

/** @transient */
export enum ItemsCacheKeys {
  ROOT_FOLDERS = 'root-folder-cache',
  BOARD_ITEMS = 'board-items-cache',
  KANBAN_BOARD_ITEMS = 'kanban-board-items-cache',
  DRAWER_ITEMS = 'drawer-items-cache',
  CYCLE_PLANNING_BACKLOG = 'cycle-planning-backlog-cache',
  PlAN_ITEMS_TAB = 'plan-items-tab-cache',
  MY_ITEMS_TAB = 'my-items-tab-cache',
  PORTFOLIO_ITEMS = 'portfolio-items-cache',
  ITEM_DRAWER_SUBHEADER_LOCATION_SELECTOR = 'item-drawer-subheader-location-selector-cache',
  ITEM_TABLE_LOCATION_SELECTOR = 'item-table-location-selector-cache',
  ITEM_DRAWER_RELATIONSHIPS = 'item-drawer-relationships-cache',
  GLOBAL_SEARCH_ITEM_SELECTOR = 'global-search-item-selector-cache',
  BULK_UPDATE_LOCATION_SELECTOR = 'bulk-update-location-selector-cache',
  FILTER_BAR_MULTI_ITEM_SELECTOR = 'filter-bar-multi-item-selector-cache',
  AUTOMATION_ITEM_FROM_PARENT_SELECTOR = 'automation-item-from-parent-selector-cache',
  AUTOMATION_ITEM_TO_PARENT_SELECTOR = 'automation-item-to-parent-selector-cache',
  AUTOMATION_CONDITION_PARENT_SELECTOR = 'automation-condition-parent-selector-cache',
  AUTOMATION_ACTION_PARENT_SELECTOR = 'automation-action-parent-selector-cache',
  QUICK_ADD_PARENT_SELECTOR = 'quick-add-parent-selector-cache',
  QUICK_ADD_SELECTED_PARENT = 'quick-add-selected-parent-cache',
  DUPLICATE_ITEM_LOCATION = 'duplicate-workstream-location-cache',
  ITEM_RELATIONSHIP_SELECTOR = 'item-relationship-selector-cache',
  ITEM_RELATIONSHIP_VISUALIZER = 'item-relationship-visualizer-cache',
  ITEM_TYPE_CHANGE_LOCATION_SELECTOR = 'item-type-change-location-selector-cache',
  ITEM_TYPE_CHANGE_SELECTED_PARENT = 'item-type-change-selected-parent-cache',
  TRELLO_IMPORT_LOCATION_SELECTOR = 'trello-import-location-selector-cache',
  ITEM_REQUEST_PARENT_LOCATION = 'item-request-parent-location-cache',
  FETCH_ITEM_LIST_BY_NUMBERS = 'fetch-item-list-by-numbers-cache',
  UPLOAD_TEMPLATE_LOCATION_SELECTOR = 'upload-template-location-selector-cache',

  GANTT_ITEMS = 'gantt-items-cache',
  GANTT_ITEMS_SELECTOR = 'gantt-items-selector-cache',

  TWO_D_BOARD_RELATED_ITEMS = 'two-d-board-related-items-cache',
  TWO_D_BOARD_PARENT_ITEMS = 'two-d-board-parent-items-cache',

  JIRA_LOCATION_MAPPER = 'jira-location-mapper-cache',
  JIRA_LOCATION_MAPPER_STORAGE = 'jira-location-mapper-cache-storage  ',
  ADO_LOCATION_MAPPER = 'ado-location-mapper-cache',
  ADO_LOCATION_MAPPER_STORAGE = 'ado-location-mapper-cache-storage',
}

/** @transient */
export type ResponseAggregationValues = {
  estimate?: number;
  estimateSD?: number;
  pointEffort?: number;
  timeSpentSeconds?: number;
  itemCount?: number;
  assigneeCount?: number;
  assigneeUsernames?: Array<string>;
  parentNumbers?: Array<string>;
  boardIds?: Array<string>;
  customFieldOptions?: Record<string, Array<string>>;
  color?: Array<string>;
  priorityIds?: Array<string>;
  laneIds?: Array<string>;
  workTypeIds?: Array<string>;
};

/** @transient */
export interface PreparedRelationship extends Relationship {
  /**
   * A unique identifier for the relationship, created on the frontend.
   */
  key: string;

  /**
   * The item on which the relationship is defined.
   */
  contextItem: PreparedItem;

  /**
   * The item that is related to the context item.
   */
  referenceItem: PreparedItem;
}
